'use client'

import { useRef, useState, type KeyboardEvent } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { FOCUS_RING, PRIMARY_CTA, SECONDARY_CTA } from '@/components/landing/cta'
import { cn } from '@/lib/utils'
import {
  TIER_ORDER,
  getAnnualMonthlyPrice,
  getTierDisplayName,
  getTierFeatures,
  getTierPrice,
  type BillingInterval,
  type SubscriptionTier,
} from '@/lib/subscription-config'

const CONTACT_EMAIL = 'dev.haven.app@gmail.com'
const INTERVALS = ['month', 'year'] as const satisfies readonly BillingInterval[]

// What the item limit doesn't count, from the same config the billing page reads.
const LIMIT_NOTE = getTierFeatures('free').find((f) => f.info)?.info

// Shorter than getTierDescription: one line under the price, not a paragraph.
const TAGLINES: Record<SubscriptionTier, string> = {
  free: 'The basics for one home.',
  pro: 'Full control of one home.',
  max: 'Several homes, heavy use.',
  unlimited: 'Large inventories, with support.',
}

function price(tier: SubscriptionTier, interval: BillingInterval) {
  if (tier === 'free') return { amount: 'Free', note: null, billed: null }
  if (tier === 'unlimited') return { amount: 'Custom', note: null, billed: null }
  if (interval === 'year') {
    return { amount: `$${getAnnualMonthlyPrice(tier)}`, note: '/ month', billed: `Billed $${getTierPrice(tier, 'year')} yearly` }
  }
  return { amount: `$${getTierPrice(tier, 'month')}`, note: '/ month', billed: null }
}

function TierAction({ tier }: { tier: SubscriptionTier }) {
  if (tier === 'free') {
    return (
      <Button asChild className={cn('h-11 w-full', PRIMARY_CTA)}>
        <Link href="/sign-up">Get started free</Link>
      </Button>
    )
  }
  if (tier === 'unlimited') {
    return (
      <Button asChild variant="outline" className={cn('h-11 w-full', SECONDARY_CTA)}>
        <a href={`mailto:${CONTACT_EMAIL}?subject=Haven Portfolio Plan Inquiry`}>Contact us</a>
      </Button>
    )
  }
  return (
    <Button asChild variant="outline" className={cn('h-11 w-full', SECONDARY_CTA)}>
      <Link href="/sign-up">Choose {getTierDisplayName(tier)}</Link>
    </Button>
  )
}

/** Landing-page pricing: one bordered table instead of the billing page's cards. */
export function LandingPricing() {
  const [interval, setBilling] = useState<BillingInterval>('month')
  const toggleRefs = useRef<(HTMLButtonElement | null)[]>([])

  // Radio-group keyboard pattern: one tab stop, arrow keys move and select.
  const onToggleKey = (e: KeyboardEvent) => {
    // Leave Alt/Cmd/Ctrl+Arrow to the browser (Back/Forward, text navigation).
    if (e.altKey || e.metaKey || e.ctrlKey) return
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = (INTERVALS.indexOf(interval) + step + INTERVALS.length) % INTERVALS.length
    setBilling(INTERVALS[next])
    toggleRefs.current[next]?.focus()
  }

  return (
    <div>
      <div
        role="radiogroup"
        aria-label="Billing period"
        onKeyDown={onToggleKey}
        className="inline-flex rounded-full border border-border bg-card p-1 text-sm"
      >
        {INTERVALS.map((value, i) => (
          <button
            key={value}
            ref={(el) => {
              toggleRefs.current[i] = el
            }}
            type="button"
            role="radio"
            aria-checked={interval === value}
            tabIndex={interval === value ? 0 : -1}
            onClick={() => setBilling(value)}
            className={cn(
              FOCUS_RING,
              'min-h-11 rounded-full px-4 transition-colors duration-150',
              interval === value ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {value === 'month' ? 'Monthly' : 'Annual, save 20%'}
          </button>
        ))}
      </div>

      <div className="mt-8 grid overflow-hidden rounded-2xl border border-border bg-card md:grid-cols-2 xl:grid-cols-4">
        {TIER_ORDER.map((tier, i) => {
          const { amount, note, billed } = price(tier, interval)
          return (
            <div
              key={tier}
              className={cn(
                'flex flex-col p-6 lg:p-8',
                i > 0 && 'border-t border-border',
                i % 2 === 1 && 'md:border-l',
                i < 2 && 'md:border-t-0',
                'xl:border-t-0',
                i > 0 && 'xl:border-l'
              )}
            >
              <p className="font-medium text-foreground">{getTierDisplayName(tier)}</p>
              <p className="mt-3">
                <span className="font-serif text-5xl text-foreground">{amount}</span>
                {note && <span className="ml-1 text-sm text-muted-foreground">{note}</span>}
              </p>
              {/* Reserved on every tier from md up, so side-by-side columns stay aligned when the period changes */}
              <p className={cn('mt-1 text-xs text-muted-foreground md:min-h-5', !billed && 'hidden md:block')}>{billed}</p>
              <p className="mt-2 text-sm text-muted-foreground">{TAGLINES[tier]}</p>
              <ul className="mt-6 flex-1 space-y-2 text-sm text-foreground">
                {getTierFeatures(tier).map((feature) => (
                  <li key={feature.text} className="flex items-baseline justify-between gap-3">
                    <span>{feature.text}</span>
                    {feature.chip && <span className="text-xs text-muted-foreground">{feature.chip.label}</span>}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <TierAction tier={tier} />
              </div>
            </div>
          )
        })}
      </div>
      {LIMIT_NOTE && <p className="mt-4 text-xs text-muted-foreground">{LIMIT_NOTE}</p>}
    </div>
  )
}
